import Quill from "quill2";
import Table from "quill2/modules/table";

const TABLE_INSERT_ROWS_ICON =
  '<svg viewbox="0 0 18 18"><g class="ql-fill ql-stroke ql-thin ql-transparent"><rect height="3" rx="0.5" ry="0.5" width="7" x="4.5" y="2.5"></rect><rect height="3" rx="0.5" ry="0.5" width="7" x="4.5" y="12.5"></rect></g><rect class="ql-fill ql-stroke ql-thin" height="3" rx="0.5" ry="0.5" width="7" x="8.5" y="7.5"></rect><polygon class="ql-fill ql-stroke ql-thin" points="4.5 11 2.5 9 4.5 7 4.5 11"></polygon><line class="ql-stroke" x1="6" x2="4" y1="9" y2="9"></line></svg>';

const TABLE_INSERT_COLUMNS_ICON =
  '<svg viewbox="0 0 18 18"><g class="ql-fill ql-transparent"><rect height="10" rx="1" ry="1" width="4" x="12" y="2"></rect><rect height="10" rx="1" ry="1" width="4" x="2" y="2"></rect></g><path class="ql-fill" d="M11.354,4.146l-2-2a0.5,0.5,0,0,0-.707,0l-2,2A0.5,0.5,0,0,0,7,5H8V6a1,1,0,0,0,2,0V5h1A0.5,0.5,0,0,0,11.354,4.146Z"></path><rect class="ql-fill" height="8" rx="1" ry="1" width="4" x="7" y="8"></rect></svg>';

const TABLE_DELETE_ROWS_ICON =
  '<svg viewbox="0 0 18 18"><g class="ql-fill ql-stroke ql-thin ql-transparent"><rect height="3" rx="0.5" ry="0.5" width="7" x="4.5" y="2.5"></rect><rect height="3" rx="0.5" ry="0.5" width="7" x="4.5" y="12.5"></rect></g><rect class="ql-fill ql-stroke ql-thin" height="3" rx="0.5" ry="0.5" width="7" x="8.5" y="7.5"></rect><line class="ql-stroke ql-thin" x1="6.5" x2="3.5" y1="7.5" y2="10.5"></line><line class="ql-stroke ql-thin" x1="3.5" x2="6.5" y1="7.5" y2="10.5"></line></svg>';

const TABLE_DELETE_COLUMNS_ICON =
  '<svg viewbox="0 0 18 18"><g class="ql-fill ql-transparent"><rect height="10" rx="1" ry="1" width="4" x="2" y="6"></rect><rect height="10" rx="1" ry="1" width="4" x="12" y="6"></rect></g><rect class="ql-fill" height="8" rx="1" ry="1" width="4" x="7" y="2"></rect><path class="ql-fill" d="M9.707,13l1.146-1.146a0.5,0.5,0,0,0-.707-0.707L9,12.293,7.854,11.146a0.5,0.5,0,0,0-.707.707L8.293,13,7.146,14.146a0.5,0.5,0,1,0,.707.707L9,13.707l1.146,1.146a0.5,0.5,0,0,0,.707-0.707Z"></path></svg>';

export type AdditionalNotesToolbarTitles = {
  table: string;
  insertRowAbove: string;
  insertRowBelow: string;
  insertColumnLeft: string;
  insertColumnRight: string;
  deleteRow: string;
  deleteColumn: string;
};

function flipSvgY(svg: string): string {
  return svg
    .replace(
      "<svg viewbox=\"0 0 18 18\">",
      "<svg viewbox=\"0 0 18 18\"><g transform=\"scale(1,-1) translate(0,-18)\">",
    )
    .replace("</svg>", "</g></svg>");
}

function flipSvgX(svg: string): string {
  return svg
    .replace(
      "<svg viewbox=\"0 0 18 18\">",
      "<svg viewbox=\"0 0 18 18\"><g transform=\"scale(-1,1) translate(-18,0)\">",
    )
    .replace("</svg>", "</g></svg>");
}

function registerTableToolbarIcons(): void {
  const icons = Quill.import("ui/icons") as Record<string, string>;
  icons["insertRowAbove"] = flipSvgY(TABLE_INSERT_ROWS_ICON);
  icons["insertRowBelow"] = TABLE_INSERT_ROWS_ICON;
  icons["insertColumnLeft"] = flipSvgX(TABLE_INSERT_COLUMNS_ICON);
  icons["insertColumnRight"] = TABLE_INSERT_COLUMNS_ICON;
  icons["deleteRow"] = TABLE_DELETE_ROWS_ICON;
  icons["deleteColumn"] = TABLE_DELETE_COLUMNS_ICON;
}

function createTableToolbarHandlers() {
  const getTableModule = (quill: Quill) => quill.getModule("table") as Table;

  return {
    table(this: { quill: Quill }) {
      getTableModule(this.quill)?.insertTable(3, 3);
    },
    insertRowAbove(this: { quill: Quill }) {
      getTableModule(this.quill)?.insertRowAbove();
    },
    insertRowBelow(this: { quill: Quill }) {
      getTableModule(this.quill)?.insertRowBelow();
    },
    insertColumnLeft(this: { quill: Quill }) {
      getTableModule(this.quill)?.insertColumnLeft();
    },
    insertColumnRight(this: { quill: Quill }) {
      getTableModule(this.quill)?.insertColumnRight();
    },
    deleteRow(this: { quill: Quill }) {
      getTableModule(this.quill)?.deleteRow();
    },
    deleteColumn(this: { quill: Quill }) {
      getTableModule(this.quill)?.deleteColumn();
    },
  };
}

export function normalizeAdditionalNotesHtml(html: string): string {
  if (!html) {
    return "";
  }

  return html
    .replace(/<span class="ql-ui"[^>]*><\/span>/gi, "")
    .replace(/\s*data-row="[^"]*"/gi, "")
    .replace(/\s*data-list="[^"]*"/gi, "")
    .replace(/\s*contenteditable="false"/gi, "");
}

export function normalizeQuillEditorHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || trimmed === "<p><br></p>" || trimmed === "<p></p>") {
    return "";
  }
  return html;
}

export function setTableToolbarTitles(
  toolbar: HTMLElement,
  titles: AdditionalNotesToolbarTitles,
): void {
  const titleMap: Record<string, string> = {
    table: titles.table,
    insertRowAbove: titles.insertRowAbove,
    insertRowBelow: titles.insertRowBelow,
    insertColumnLeft: titles.insertColumnLeft,
    insertColumnRight: titles.insertColumnRight,
    deleteRow: titles.deleteRow,
    deleteColumn: titles.deleteColumn,
  };

  Object.entries(titleMap).forEach(([name, title]) => {
    toolbar.querySelector(`button.ql-${name}`)?.setAttribute("title", title);
  });

  const formats = toolbar.querySelectorAll(".ql-formats");
  const tableGroup = formats[formats.length - 1];
  if (tableGroup?.querySelector(".ql-table")) {
    tableGroup.classList.add("ql-table-formats");
  }
}

function buildToolbarElement(): HTMLElement {
  const toolbar = document.createElement("div");
  toolbar.setAttribute("role", "toolbar");
  toolbar.classList.add("ql-toolbar", "ql-snow");

  const groups: Array<Array<string | Record<string, string>>> = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [
      "table",
      "insertRowAbove",
      "insertRowBelow",
      "insertColumnLeft",
      "insertColumnRight",
      "deleteRow",
      "deleteColumn",
    ],
  ];

  groups.forEach((controls) => {
    const group = document.createElement("span");
    group.classList.add("ql-formats");

    controls.forEach((control) => {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("aria-pressed", "false");

      if (typeof control === "string") {
        button.classList.add(`ql-${control}`);
        button.setAttribute("aria-label", control);
      } else {
        const format = Object.keys(control)[0];
        const value = control[format];
        button.classList.add(`ql-${format}`);
        if (value != null) {
          button.value = value;
          button.setAttribute("aria-label", `${format}: ${value}`);
        } else {
          button.setAttribute("aria-label", format);
        }
      }

      group.appendChild(button);
    });

    toolbar.appendChild(group);
  });

  return toolbar;
}

export function createAdditionalNotesQuill(
  editor: HTMLElement,
  toolbarHost: HTMLElement,
  placeholder: string,
  titles: AdditionalNotesToolbarTitles,
): Quill {
  Table.register();
  registerTableToolbarIcons();

  toolbarHost.innerHTML = "";
  const toolbar = buildToolbarElement();
  toolbarHost.appendChild(toolbar);

  const quill = new Quill(editor, {
    theme: "snow",
    placeholder,
    modules: {
      table: true,
      history: {
        delay: 500,
        maxStack: 200,
        userOnly: true,
      },
      toolbar: {
        container: toolbar,
        handlers: createTableToolbarHandlers(),
      },
    },
  });

  setTableToolbarTitles(toolbar, titles);

  return quill;
}

export function setQuillHtml(quill: Quill, html: string): void {
  const normalized = normalizeQuillEditorHtml(html);
  if (normalized) {
    const delta = quill.clipboard.convert({ html: normalized });
    quill.setContents(delta, "api");
  } else {
    quill.setText("", "api");
  }
}
